import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Toast, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaPlus, FaEdit } from 'react-icons/fa';
import { getActiveNews, deleteNews, updateNews } from '../../services/newsService';
import moment from 'moment';
import 'moment/locale/tr';
import './News.scss';

moment.locale('tr');

const NewsList = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [editNews, setEditNews] = useState({
    guid: '',
    header: '',
    detail: '',
    shortDetail: '',
    date: '',
    newsCategoryId: '',
    refererLink: '',
    picture: null,
    pictureBase64: '',
    pictureUrl: ''
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await getActiveNews();
      console.log('API Response:', response);
      if (response?.isSuccess) {
        setNews(response.data.items || []);
        setError(null);
      } else {
        setError(response?.resultMessage || 'Haberler yüklenirken bir hata oluştu.');
        setNews([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Haberler yüklenirken bir hata oluştu.');
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteNews(selectedNews.guid);
      if (response.isSuccess) {
        setNews(news.filter(n => n.guid !== selectedNews.guid));
        setToastMessage('Haber başarıyla silindi!');
        setToastVariant('success');
        setShowToast(true);
      } else {
        const errorMessage = response.resultMessage || 'Haber silinirken bir hata oluştu.';
        setToastMessage(errorMessage);
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error deleting news:', error);
      const errorMessage = error.response?.data?.message || 'Haber silinirken bir hata oluştu.';
      setToastMessage(errorMessage);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setShowDeleteModal(false);
      setSelectedNews(null);
    }
  };

  const confirmDelete = (newsItem) => {
    setSelectedNews(newsItem);
    setShowDeleteModal(true);
  };

  const handleEdit = (newsItem) => {
    setSelectedNews(newsItem);
    setEditNews({
      guid: newsItem.guid,
      header: newsItem.header,
      detail: newsItem.detail,
      shortDetail: newsItem.shortDetail || '',
      date: newsItem.date,
      newsCategoryId: newsItem.newsCategoryId,
      refererLink: newsItem.refererLink || '',
      picture: null,
      pictureBase64: '',
      pictureUrl: newsItem.pictureUrl || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Mevcut haber verilerini kullanarak güncelleme verilerini oluştur
      const updateData = { ...editNews };
      
      // Eğer yeni bir resim seçilmemişse ve mevcut resim varsa, mevcut resmi kullan
      if (!updateData.picture && selectedNews?.pictureUrl) {
        // Mevcut resim bilgisini koru
        updateData.existingPictureUrl = selectedNews.pictureUrl;
        console.log('Mevcut resim korunuyor:', selectedNews.pictureUrl);
      } else if (!updateData.picture && !selectedNews?.pictureUrl) {
        // Eğer resim seçilmemiş ve mevcut resim de yoksa, boş gönder
        updateData.picture = null;
        updateData.pictureBase64 = '';
        updateData.existingPictureUrl = '';
        console.log('Resim seçilmedi ve mevcut resim yok, boş gönderiliyor');
      }
      
      console.log('Güncelleme için gönderilen veri:', {
        ...updateData,
        picture: updateData.picture ? 'Yeni resim seçildi' : 'Resim seçilmedi',
        existingPictureUrl: updateData.existingPictureUrl || 'Yok'
      });
      
      const response = await updateNews(updateData);
      
      if (response?.data?.isSuccess) {
        setToastMessage('Haber başarıyla güncellendi!');
        setToastVariant('success');
        setShowToast(true);
        await fetchNews();
        setShowEditModal(false);
      } else {
        const errorMessage = response?.data?.resultMessage || 'Haber güncellenirken bir hata oluştu.';
        setToastMessage(errorMessage);
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error updating news:', error);
      const errorMessage = error.response?.data?.message || 'Haber güncellenirken bir hata oluştu.';
      setToastMessage(errorMessage);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditNews(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePictureChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setEditNews(prev => ({
        ...prev,
        picture: e.target.files[0]
      }));
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">Yükleniyor...</div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <div
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999
        }}
      >
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={1500}
          autohide
          bg={toastVariant}
        >
          <Toast.Body className="text-white">
            {toastMessage}
          </Toast.Body>
        </Toast>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Haberler</h2>
        <Link to="/news/add">
          <Button variant="primary">
            <FaPlus className="me-2" />
            Yeni Haber Ekle
          </Button>
        </Link>
      </div>

      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : news.length === 0 ? (
        <div className="text-center mt-5">
          <p>Henüz haber bulunmuyor.</p>
        </div>
      ) : (
        <Row>
          {news.map((item) => (
            <Col key={item.guid} lg={4} md={6} className="mb-4">
              <Card className="h-100 news-card">
                <div className="news-image-container">
                  <Card.Img 
                    variant="top" 
                    src={item.pictureUrl} 
                    className="news-image"
                    onError={(e) => {
                      e.target.src = '/placeholder.jpg';
                    }}
                  />
                </div>
                <Card.Body>
                  <Card.Title>{item.header}</Card.Title>
                  <div className="news-details">
                    <p className="mb-2">
                      <strong>Tarih:</strong> {moment(item.date).format('DD MMMM YYYY, HH:mm')}
                    </p>
                    {item.detail && (
                      <p className="news-detail mb-2">
                        <strong>Detay:</strong> {item.detail}
                      </p>
                    )}
                  </div>
                </Card.Body>
                <Card.Footer className="bg-white">
                  <div className="d-flex justify-content-between">
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => confirmDelete(item)}
                      className="me-2"
                    >
                      <FaTrash className="me-1" /> Sil
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => handleEdit(item)}
                    >
                      <FaEdit className="me-1" /> Düzenle
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Haber Silme</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu haberi silmek istediğinizden emin misiniz?
          <br />
          <strong>{selectedNews?.header}</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Sil
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit News Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Haber Düzenle</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Başlık</Form.Label>
              <Form.Control 
                type="text" 
                name="header" 
                value={editNews.header} 
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Detay</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={5} 
                name="detail" 
                value={editNews.detail} 
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kısa Detay (Opsiyonel)</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={2} 
                name="shortDetail" 
                value={editNews.shortDetail} 
                onChange={handleInputChange}
                placeholder="Boş bırakılırsa detaydan kısaltılacaktır"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Haber Tarihi</Form.Label>
              <Form.Control 
                type="date" 
                name="date" 
                value={editNews.date ? new Date(editNews.date).toISOString().split('T')[0] : ''} 
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Referans Bağlantı (Opsiyonel)</Form.Label>
              <Form.Control 
                type="text" 
                name="refererLink" 
                value={editNews.refererLink} 
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Resim {editNews.pictureUrl && '(Mevcut resim var)'}</Form.Label>
              <Form.Control 
                type="file" 
                accept="image/*"
                onChange={handlePictureChange}
              />
              {editNews.pictureUrl && (
                <div className="mt-2">
                  <img 
                    src={editNews.pictureUrl} 
                    alt="Mevcut resim" 
                    style={{ maxWidth: '200px', maxHeight: '150px' }} 
                    className="img-thumbnail"
                  />
                </div>
              )}
              <Form.Text className="text-muted">
                Yeni bir resim seçmezseniz mevcut resim korunacaktır.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              İptal
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Güncelleniyor...' : 'Güncelle'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default NewsList;
